import React, { useRef, useState } from 'react'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import { IconButton, Skeleton } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import {
  ButtonWithLoader,
  type ClassesType,
  type PuiPopperProps,
} from '@pbt/pbt-ui-components'

import ActionsPopper, { type PopperAction } from '../ActionsPopper'

const useStyles = makeStyles(
  (theme) => ({
    icon: {
      width: 20,
      height: 20,
    },
    arrow: {
      fontSize: '2rem',
    },
    iconButton: {
      padding: theme.spacing(0.5),
      margin: theme.spacing(0.5),
    },
  }),
  { name: 'ActionsButton' },
)

export interface ActionsButtonProps {
  ButtonComponent?: React.JSXElementConstructor<any>
  Icon?: React.JSXElementConstructor<any>
  actions?: (() => PopperAction[]) | PopperAction[]
  children?: React.ReactNode
  className?: string
  classes?: ClassesType<typeof useStyles>
  closeOnTooltipsEvents?: PuiPopperProps['closeOnTooltipsEvents']
  disabled?: boolean
  isLoading?: boolean
  isOpen?: boolean
  loading?: boolean
  placement?: PuiPopperProps['placement']
  rootClassName?: string
  setIsOpen?: (newState: boolean) => void
  useIconButton?: boolean
  useNormalButton?: boolean
}

const ActionsButton = ({
  useIconButton = true,
  useNormalButton = false,
  ButtonComponent,
  Icon = MoreHoriz,
  actions: actionsProp,
  classes: classesProp,
  isLoading,
  placement,
  className,
  closeOnTooltipsEvents = false,
  isOpen: isOpenProp,
  setIsOpen: setIsOpenProp,
  ...rest
}: ActionsButtonProps) => {
  const classes = useStyles({ classes: classesProp })

  const [internalOpen, setInternalOpen] = useState(false)

  const popperOpen = isOpenProp !== undefined ? isOpenProp : internalOpen

  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleTogglePopper = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const newState = !popperOpen
    if (setIsOpenProp) {
      setIsOpenProp(newState)
    } else {
      setInternalOpen(newState)
    }
  }

  const handleClosePopper = () => {
    if (setIsOpenProp) {
      setIsOpenProp(false)
    } else {
      setInternalOpen(false)
    }
  }

  const getActions = () =>
    typeof actionsProp === 'function' ? actionsProp() : actionsProp

  return (
    <>
      {isLoading ? (
        <Skeleton height={20} variant="circular" width={20} />
      ) : useNormalButton ? (
        <ButtonWithLoader
          className={className}
          endIcon={
            popperOpen ? (
              <KeyboardArrowUp className={classes.arrow} />
            ) : (
              <KeyboardArrowDown className={classes.arrow} />
            )
          }
          ref={buttonRef}
          onClick={handleTogglePopper}
          {...rest}
        />
      ) : useIconButton ? (
        <IconButton
          className={classNames(className, classes.iconButton)}
          ref={buttonRef}
          size="large"
          onClick={handleTogglePopper}
          {...rest}
        >
          <Icon className={classes.icon} />
        </IconButton>
      ) : ButtonComponent ? (
        <ButtonComponent
          className={className}
          ref={buttonRef}
          onClick={handleTogglePopper}
          {...rest}
        />
      ) : null}
      {popperOpen && (
        <ActionsPopper
          actions={getActions()}
          anchorEl={buttonRef.current}
          closeOnTooltipsEvents={closeOnTooltipsEvents}
          placement={placement}
          onClose={handleClosePopper}
        />
      )}
    </>
  )
}

export default ActionsButton
